import { supabase } from '@/lib/supabaseClient';
import type { RefereeProposal, RefereeProposalAid, RefereeProposalStatus, UsualTrio } from '@/lib/refereeProposals/refereeProposals';

/** Columns of referee_assignment_proposals (migration 20261030170000), named explicitly. */
const COLUMNS =
    'id, match_id, role, official_id, league_id, season, status, replaced_by_official_id, proposed_by, decided_by, decided_at, created_at, updated_at';

interface ProposalRow {
    id: string;
    match_id: string;
    role: string;
    official_id: string;
    league_id: string;
    season: string;
    status: RefereeProposalStatus;
    replaced_by_official_id: string | null;
    proposed_by: string | null;
    decided_by: string | null;
    decided_at: string | null;
    created_at: string;
    updated_at: string;
}

export const mapRefereeProposal = (r: ProposalRow): RefereeProposal => ({
    id: r.id,
    matchId: r.match_id,
    role: r.role,
    officialId: r.official_id,
    leagueId: r.league_id,
    season: r.season,
    status: r.status,
    replacedByOfficialId: r.replaced_by_official_id,
    proposedBy: r.proposed_by,
    decidedBy: r.decided_by,
    decidedAt: r.decided_at,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
});

/** Every proposal (pending and closed) of these matches. */
export async function fetchProposalsForMatches(matchIds: string[]): Promise<RefereeProposal[]> {
    if (matchIds.length === 0) return [];
    const { data, error } = await supabase.from('referee_assignment_proposals').select(COLUMNS).in('match_id', matchIds);
    if (error) throw error;
    return ((data ?? []) as ProposalRow[]).map(mapRefereeProposal);
}

/** Pending proposals of a league and season (bell, match chips). */
export async function fetchPendingProposals(leagueIds: string[], season: string): Promise<RefereeProposal[]> {
    if (leagueIds.length === 0 || !season) return [];
    const { data, error } = await supabase
        .from('referee_assignment_proposals')
        .select(COLUMNS)
        .in('league_id', leagueIds)
        .eq('season', season)
        .eq('status', 'pending');
    if (error) throw error;
    return ((data ?? []) as ProposalRow[]).map(mapRefereeProposal);
}

export async function proposeReferee(matchId: string, role: string, officialId: string): Promise<string> {
    const { data, error } = await supabase.rpc('propose_referee', {
        p_match_id: matchId,
        p_role: role,
        p_official_id: officialId,
    });
    if (error) throw error;
    return data as string;
}

/** 'withdrawn' by the Responsable Arbitres, 'refused' by the league. */
export async function closeRefereeProposal(proposalId: string, status: 'withdrawn' | 'refused'): Promise<void> {
    const { error } = await supabase.rpc('close_referee_proposal', { p_proposal_id: proposalId, p_status: status });
    if (error) throw error;
}

interface AidRow {
    official_id: string;
    season_matches: number;
    last_match_date: string | null;
    home_team_matches: number;
    away_team_matches: number;
    nearest_other_date: string | null;
    official_wilaya: string | null;
    home_wilaya: string | null;
    away_wilaya: string | null;
    central_official_id: string | null;
    with_central_matches: number | null;
}

export async function fetchProposalAids(matchId: string): Promise<RefereeProposalAid[]> {
    const { data, error } = await supabase.rpc('get_referee_proposal_aids', { p_match_id: matchId });
    if (error) throw error;
    return ((data ?? []) as AidRow[]).map((r) => ({
        officialId: r.official_id,
        seasonMatches: r.season_matches ?? 0,
        lastMatchDate: r.last_match_date,
        homeTeamMatches: r.home_team_matches ?? 0,
        awayTeamMatches: r.away_team_matches ?? 0,
        nearestOtherDate: r.nearest_other_date,
        officialWilaya: r.official_wilaya,
        homeWilaya: r.home_wilaya,
        awayWilaya: r.away_wilaya,
        centralOfficialId: r.central_official_id ?? null,
        withCentralMatches: r.with_central_matches ?? 0,
    }));
}

/** Centrals of a league whose same pair of assistants covers ≥ 70 % of their matches that season. */
export async function fetchUsualTrios(leagueId: string, season: string): Promise<UsualTrio[]> {
    const { data, error } = await supabase.rpc('get_referee_usual_trios', { p_league_id: leagueId, p_season: season });
    if (error) throw error;
    return ((data ?? []) as any[]).map((r) => ({
        centralId: r.central_id,
        assistant1Id: r.assistant_1_id,
        assistant2Id: r.assistant_2_id,
        pairMatches: r.pair_matches,
        centralMatches: r.central_matches,
        season,
    }));
}

export interface PendingProposalMatch {
    matchId: string;
    count: number;
    /** Most recent proposal of the match (bell ordering). */
    latestAt: string;
    matchDate: string | null;
    home: { name: string; name_ar: string | null } | null;
    away: { name: string; name_ar: string | null } | null;
}

/** Pending proposals of a league and season, grouped per match (notification bell). */
export async function fetchPendingProposalMatches(leagueIds: string[], season: string): Promise<PendingProposalMatch[]> {
    if (leagueIds.length === 0 || !season) return [];
    const { data, error } = await supabase
        .from('referee_assignment_proposals')
        .select(
            'match_id, updated_at, match:matches(match_date, home_team:teams!matches_home_team_id_fkey(name,name_ar), away_team:teams!matches_away_team_id_fkey(name,name_ar))',
        )
        .in('league_id', leagueIds)
        .eq('season', season)
        .eq('status', 'pending');
    if (error) throw error;
    const byMatch = new Map<string, PendingProposalMatch>();
    for (const r of (data ?? []) as any[]) {
        const cur = byMatch.get(r.match_id);
        if (cur) {
            cur.count += 1;
            if (r.updated_at > cur.latestAt) cur.latestAt = r.updated_at;
        } else {
            byMatch.set(r.match_id, {
                matchId: r.match_id,
                count: 1,
                latestAt: r.updated_at,
                matchDate: r.match?.match_date ?? null,
                home: r.match?.home_team ?? null,
                away: r.match?.away_team ?? null,
            });
        }
    }
    return [...byMatch.values()];
}
